/**
 * Field sessions: working cattle through the chute or in a pen against a roster, with readers and
 * scales and without signal. Sessions own no facts of their own. Each animal's results are other
 * modules' events (weights, treatments, preg checks, notes) recorded with the session's job id and a
 * shared batch id, so they land in the same history as anything else.
 */
import { defineModule, f, type JobTypeDef } from "../../contract";

const sessionFields = {
  template_id: f.text("Session template", {
    level: "more",
    agent: "Template id from sessions_templates when one fits what they described (weigh, preg check, vaccinate). Leave out otherwise.",
  }),
  place_id: f.ref("Where", "place", { level: "more" }),
  notes: f.longText("Notes for the crew", { level: "more" }),
};

const chute: JobTypeDef = {
  type: "sessions.chute",
  label: "Chute session",
  description: "Run cattle through the chute: identify each one, record weights, treatments and checks, and keep going without signal.",
  icon: "scale",
  tone: "perf",
  workspace: "sessions",
  subjectKind: "animal",
  fields: sessionFields,
  agent: {
    when: "They want to set up working cattle through the chute or alley: a weighing, preg checking, vaccinating or processing day for a group. Put the animals in subjects.",
  },
};

const pen: JobTypeDef = {
  type: "sessions.pen",
  label: "Pen or pasture session",
  description: "Go through a pen or pasture with a list: tag, check, note or treat each animal where it stands.",
  icon: "fence",
  tone: "perf",
  workspace: "sessions",
  subjectKind: "animal",
  fields: sessionFields,
  agent: {
    when: "They want a worklist to go through animals in a pen or pasture without a chute: checking pairs, tagging calves, looking cattle over.",
  },
};

export default defineModule({
  id: "sessions",
  label: "Field sessions",
  description: "Chute and field work against a roster, with readers and scales.",
  jobs: [chute, pen],
  workspaces: [
    {
      id: "sessions",
      label: "Field sessions",
      description: "Work cattle through the chute or in a pen: a list, what to record, a reader and a scale, with or without signal.",
      icon: "scale",
      group: "care",
      keywords: ["chute", "session", "work cattle", "processing", "weigh", "scale", "reader", "eid", "wand", "offline", "alley", "squeeze"],
      keywordsEs: ["manga", "trabajar ganado", "báscula", "bascula", "lector", "chute", "pesar", "sesión", "sesion", "corral", "sin señal", "prensa"],
      presets: ["seedstock", "commercial", "feedlot"],
    },
  ],
  ranchSections: [
    {
      id: "devices",
      label: "Readers and scales",
      description: "Connect an EID reader or scale indicator, test a reading and set units. Typing always works too.",
      icon: "radio",
      group: "equipment",
      keywords: ["reader", "scale", "eid", "rfid", "wand", "bluetooth", "serial", "indicator", "weigh"],
      keywordsEs: ["lector", "báscula", "bascula", "arete electrónico", "bluetooth", "indicador", "pesar"],
    },
  ],
  attributes: {
    animal: {
      provisional: f.bool("Added at the chute, identity to confirm", { level: "more", help: "Set when an unknown tag was added during a session. Confirm it or merge it into the right animal." }),
      session_id: f.ref("Added during session", "job", { level: "more" }),
    },
  },
  settings: {
    scale_unit: f.enum("Scale reads in", { lb: "Pounds", kg: "Kilograms" }, { help: "Used when a scale doesn't send its units." }),
  },
  messages: [
    // Problems the server reports for a step or a result; shown through t().
    "That kind of record isn't available on this ranch yet.",
    "This record isn't about one animal.",
    "Enter it for each animal or set it for all.",
    "Unknown field",
    "Invalid",
    "This animal is marked {{status}}. Check that the right animal was worked.",
    "This animal already has results in this session.",
    "We couldn't find that animal on this ranch.",
    "Some details need fixing.",
    "You don't have access to record this.",
    "That kind of record isn't set up on this ranch yet.",
    "An animal with this tag or EID is already on the ranch.",
    "This session was cancelled.",
    "Waiting for the animal added at the chute to arrive first.",
    "Still being filed. We'll check again.",
    // Sold, dead and other statuses inside the message above.
    "sold", "dead", "culled", "transferred",
  ],
});
